/**
 * Icon Registry
 * Maps backend icon names to Lucide React icon components
 * This bridges the gap between backend lowercase icon names and Lucide's PascalCase names
 */

import {
  LayoutDashboard,
  Settings,
  Building2,
  IndianRupee,
  Shield,
  BarChart,
  UserCog,
  FileSearch,
  GitBranch,
  UserCircle,
  UsersRound,
  CreditCard,
  FileText,
  FileBarChart,
  Megaphone,
  Puzzle,
  School,
  Layers3,
  FileCheck,
  UserCheck,
  Wallet,
  BookOpen,
  Bus,
  Bed,
  Boxes,
  ShieldCheck,
  Send,
  ClipboardList,
  Link,
  Settings2,
  GraduationCap,
  Users,
  ToggleLeft,
  Package,
  Image,
  BadgePercent,
  Menu,
  MoreVertical,
  LifeBuoy,
  MessageSquare,
  Mail,
  Phone,
  Key,
  History,
  Activity,
  Circle,
  PlusCircle,
  RotateCcw,
  RotateCw,
  Undo2,
  Redo2,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  UploadCloud,
  TreeDeciduous,
  Umbrella,
  CalendarCheck,
  CalendarDays,
  Terminal,
  Wrench,
  // School Management Icons
  Award,
  Trophy,
  Medal,
  Pencil,
  Ruler,
  Calculator,
  Microscope,
  FlaskConical,
  Globe,
  Map,
  Compass,
  Music,
  Paintbrush,
  Palette,
  Dumbbell,
  Camera,
  Video,
  Youtube,
  Headphones,
  Printer,
  ScanLine,
  ClipboardCheck,
  Star,
  // DataTable Icons
  Search,
  Filter,
  Columns,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  PanelLeftOpen,
  PanelLeftClose,
  Trash2,
  Edit,
  Plus,
  Download,
  Loader2,
  X,
  Save,
  Eye,
  EyeOff,
  // Missing Icons
  UserPlus,
  UserMinus,
  Home,
  Briefcase,
  Book,
  Workflow,
  Grid3x3,
  Bell,
  HelpCircle,
  PackageCheck,
  Type,
  Asterisk,
  AlertCircle,
  AlertTriangle,
  Lightbulb,
  Link2,
  Info,
  Sparkles,
  Check,
  Lock,
  LogOut,
  Clock,
  CheckCircle,
  XCircle,
  FileClock,
  DollarSign,
  Heart,
  Copy,
  TrendingUp,
  TrendingDown,
  Minus,
  LineChart,
  PieChart,
  FileCode,
  CheckSquare,
  ChevronUp,
  // Used by Icon name="PascalCase" and theme / workspace UI
  User,
  Sun,
  Moon,
  Monitor,
  ArrowLeftRight,
  ExternalLink,
  Wand2,
  Smartphone,
  Folder,
  FileEdit,
  List,
  Unlock,
  GripVertical,
  Play,
  MapPin,
  UserX,
  Archive,
  File,
  Inbox,
  LayoutPanelTop,
  LayoutPanelLeft,
  Share2,
  ChevronsRight,
  BarChart3,
  Pause,
  Receipt,
  Cpu,
  HardDrive,
  Hash,
  AtSign,
  type LucideIcon,
} from 'lucide-react'

/**
 * Icon registry mapping
 * Key: Backend icon name (lowercase, from API)
 * Value: Lucide icon component
 */
export const Icons: Record<string, LucideIcon> = {
  // PascalCase = Lucide export names (ContextSwitcher, platform pages, <Icon name="Building2" />, etc.)
  Building2: Building2,
  LayoutDashboard: LayoutDashboard,
  Shield: Shield,
  ShieldCheck: ShieldCheck,
  Loader2: Loader2,
  School: School,
  GitBranch: GitBranch,
  /** Person silhouette; lowercase `user` still maps to UserCircle for legacy menus */
  User: User,
  Sun: Sun,
  Moon: Moon,
  Monitor: Monitor,
  ArrowLeftRight: ArrowLeftRight,
  ChevronDown: ChevronDown,

  // Core Navigation
  dashboard: Monitor,
  layoutDashboard: LayoutDashboard,
  settings: Settings,
  profile: UserCircle,
  
  // Organization & Structure
  organization: Building2,
  business: Building2,
  building: Building2,
  branches: GitBranch,
  departments: TreeDeciduous,
  account_tree: GitBranch, // Tree structure/hierarchy icon
  
  // User & Role Management
  users: UserCog,
  usersGroup: Users,
  home: Home,
  briefcase: Briefcase,
  roles: ShieldCheck,
  groups: UsersRound,
  people: UsersRound,
  userMinus: UserMinus,
  UserMinus: UserMinus,
  /** Deactivate / remove access (distinct from delete/trash) */
  deactivate: UserMinus,
  /** Reactivate restored platform access (verified users) */
  reactivate: UserCheck,
  reactivateAccount: UserCheck,
  
  // Analytics & Audit
  analytics: BarChart,
  'bar-chart': BarChart,
  barChart: BarChart,
  reports: FileBarChart,
  fileBarChart: FileBarChart, // Direct mapping for category
  'trending-up': TrendingUp,
  trendingUp: TrendingUp,
  'trending-down': TrendingDown,
  trendingDown: TrendingDown,
  minus: Minus,
  lineChart: LineChart,
  pieChart: PieChart,
  audit_icon: FileSearch,
  audit: FileSearch,
  audit_logs: FileSearch,
  fileSearch: FileSearch, // Direct mapping for category
  
  // Payment & Billing
  payment: IndianRupee,
  revenue: IndianRupee,
  subscription: CreditCard,
  invoices: FileText,
  fileText: FileText, // Direct mapping for category
  wallet: Wallet, // Direct mapping for category
  creditCard: CreditCard,
  'credit-card': CreditCard, // Hyphenated variant
  dollar: DollarSign,
  dollarSign: DollarSign,
  currency: IndianRupee,
  
  // Security
  security: Shield,
  
  // School Management
  school: School,
  class: Layers3,
  layers: Layers3,
  assignment: FileCheck,
  fileCheck: FileCheck, // Direct mapping for category
  graduationCap: GraduationCap, // Direct mapping for category (camelCase variant)
  library_books: BookOpen,
  library: BookOpen,
  local_library: BookOpen, // Material icon name for library
  menu_book: BookOpen, // Material Design icon name mapped to BookOpen
  directions_bus: Bus,
  bus: Bus,
  hotel: Bed,
  hostel: Bed,
  inventory: Boxes,
  family_restroom: UsersRound,
  account_circle: UserCircle,
  account_balance: Wallet,
  how_to_reg: UserCheck,
  admin_panel_settings: ShieldCheck,
  extension: Puzzle,
  puzzle: Puzzle, // Direct mapping for category
  assessment: FileBarChart,
  campaign: Megaphone,
  manage: Settings,
  academics: GraduationCap,
  /** Singular alias (PageHeader / breadcrumbs) */
  academic: GraduationCap,
  /** Sections / layered classes UI */
  section: Layers3,
  _invite: Send,
  invite: Send, // Preferred alias for invite/send (backend menu icon)
  send: Send, // Direct mapping for category
  hook_icon: Link,
  link: Link, // Direct mapping for link icon
  _form_Mangement: ClipboardList,
  _manageForms: ClipboardList,
  formManagement: ClipboardList, // Preferred alias (backend menu icon)
  clipboardList: ClipboardList, // Direct mapping for category
  gear_icon: Settings2,
  settings2: Settings2, // Direct mapping for category
  fees: Wallet,
  person: UserCircle,
  user: UserCircle,
  userCheck: UserCheck,
  person_add: UserPlus, // Add person/user icon
  'person-add': UserPlus, // Hyphenated variant
  
  // UI Icons
  circle: Circle,
  plusCircle: PlusCircle,
  menu: Menu,
  moreVertical: MoreVertical,
  MoreVertical,
  more: MoreVertical,
  ellipsis: MoreVertical,
  dots: MoreVertical,
  image: Image,
  photo_library: Image,
  badge: BadgePercent,
  support: LifeBuoy,
  message: MessageSquare,
  messageSquare: MessageSquare, // camelCase for Suggestion Box / Suggestion Management
  forum: MessageSquare, // Communication/discussion forum icon
  email: Mail,
  mail: Mail,
  Mail,
  Phone,
  phone: Phone,
  key: Key,
  history: History,
  activity: Activity,
  refresh: RotateCcw,
  rotateCw: RotateCw,
  RotateCw,
  Undo2,
  undo2: Undo2,
  Redo2,
  redo2: Redo2,
  RefreshCw,
  refreshCw: RefreshCw,
  arrowRight: ArrowRight,
  upload: UploadCloud,
  plans: Package,
  featureToggles: ToggleLeft,
  
  // DataTable & Common UI Icons
  search: Search,
  filter: Filter,
  columns: Columns,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronDown: ChevronDown,
  chevronUp: ChevronUp,
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  arrowLeft: ArrowLeft,
  panelLeftOpen: PanelLeftOpen,
  panelLeftClose: PanelLeftClose,
  trash: Trash2,
  Trash2,
  edit: Edit,
  plus: Plus,
  download: Download,
  loader: Loader2,
  x: X,
  save: Save,
  eye: Eye,
  Eye,
  eyeOff: EyeOff,
  EyeOff,
  view: Eye,
  copy: Copy,
  Copy: Copy, // PascalCase variant
  
  // Additional mappings
  employees: Users,
  payroll: Wallet,
  leave: Umbrella,
  attendance: CalendarCheck,
  calendar: CalendarDays,
  calendar_today: CalendarDays, // Material icon name for calendar
  event: CalendarDays,
  console: Terminal,
  terminal: Terminal,
  
  // Utility icons
  tool: Wrench,
  Tool: Wrench, // PascalCase variant for DesignLibrary
  
  // Missing icons from seed file
  assets: PackageCheck,
  userPlus: UserPlus,
  book: Book,
  workflow: Workflow,
  grid: Grid3x3,
  notifications: Bell,
  bell: Bell,
  
  // Alert & Error Icons
  asterisk: Asterisk,
  Asterisk: Asterisk,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  AlertTriangle,
  lightbulb: Lightbulb,
  Lightbulb,
  link2: Link2,
  Link2,
  shieldCheck: ShieldCheck,
  helpCircle: HelpCircle,
  info: Info,
  information: Info,
  diamond: Sparkles,
  check: Check,
  Check: Check,
  lock:   Lock,
  logOut: LogOut,
  LogOut,
  clock: Clock,
  Clock: Clock,
  checkCircle: CheckCircle,
  CheckCircle: CheckCircle,
  xCircle: XCircle,
  XCircle: XCircle,
  fileClock: FileClock,
  FileClock: FileClock,
  
  // Placeholder/Test Icons
  test: Activity, // Placeholder icon for test menu items
  
  // School Management - Students & Teachers
  student: GraduationCap,
  teacher: UserCog,
  
  // School Management - Academics
  exam: ClipboardCheck,
  grade: Star,
  timetable: CalendarDays,
  subject: BookOpen,
  homework: BookOpen, // Using BookOpen as Notebook doesn't exist
  result: FileBarChart,
  
  // School Management - Achievements
  certificate: Award,
  award: Award,
  trophy: Trophy,
  leaderboard: Trophy,
  medal: Medal,
  star: Star,
  
  // School Management - Supplies & Equipment
  notebook: BookOpen, // Using BookOpen as Notebook doesn't exist in Lucide
  pencil: Pencil,
  ruler: Ruler,
  calculator: Calculator,
  microscope: Microscope,
  flask: FlaskConical,
  
  // School Management - Geography & Arts
  globe: Globe,
  map: Map,
  compass: Compass,
  music: Music,
  paintbrush: Paintbrush,
  palette: Palette,
  
  // Typography & Text
  text_fields: Type,
  type: Type,
  font: Type,
  
  // School Management - Sports (using Activity/Circle as sports icons don't exist in Lucide)
  dumbbell: Dumbbell,
  football: Activity, // Using Activity as Football doesn't exist
  basketball: Activity, // Using Activity as Basketball doesn't exist
  volleyball: Activity, // Using Activity as Volleyball doesn't exist
  tennis: Activity, // Using Activity as Tennis doesn't exist
  
  // School Management - Technology
  camera: Camera,
  video: Video,
  youtube: Youtube,
  Youtube: Youtube,
  headphones: Headphones,
  printer: Printer,
  scanner: ScanLine,
  
  // Medical & Health
  heart: Heart,
  Heart: Heart, // PascalCase variant
  
  // Template & Forms
  template: FileCode,
  checkSquare: CheckSquare,
  CheckSquare: CheckSquare, // PascalCase variant

  // Common action aliases (Material / legacy names)
  add: Plus,
  Add: Plus,
  Plus,
  Edit,
  List,
  list: List,
  File,
  file: File,
  inbox: Inbox,
  Inbox,
  Folder,
  folder: Folder,
  Play,
  play: Play,
  Pause,
  pause: Pause,
  Archive,
  archive: Archive,
  BookOpen,
  RotateCcw,
  ClipboardCheck,
  Layers3,
  UserCog,
  userCog: UserCog,
  ExternalLink,
  externalLink: ExternalLink,
  Wand2,
  wand2: Wand2,
  Smartphone,
  smartphone: Smartphone,
  FileEdit,
  fileEdit: FileEdit,
  lockOpen: Unlock,
  Unlock,
  unlock: Unlock,
  GripVertical,
  gripVertical: GripVertical,
  MapPin,
  mapPin: MapPin,
  Hash,
  hash: Hash,
  AtSign,
  atSign: AtSign,
  UserX,
  'user-x': UserX,
  userX: UserX,
  LayoutPanelTop,
  layoutPanelTop: LayoutPanelTop,
  LayoutPanelLeft,
  layoutPanelLeft: LayoutPanelLeft,
  Share2,
  share: Share2,
  Share: Share2,
  ChevronsRight,
  chevronsRight: ChevronsRight,
  BarChart3,
  barChart3: BarChart3,
  Receipt,
  receipt: Receipt,
  receipt_long: Receipt,
  payments: IndianRupee,
  Cpu,
  cpu: Cpu,
  HardDrive,
  hardDrive: HardDrive,
  UploadCloud,
  FlaskConical,
  warning: AlertTriangle,
  'alert-circle': AlertCircle,
  'x-circle': XCircle,
  'check-circle': CheckCircle,
  'alert-triangle': AlertTriangle,
  'refresh-cw': RefreshCw,
  'file-text': FileText,
  arrow_forward: ArrowRight,
  arrow_back: ArrowLeft,
  'arrow-left': ArrowLeft,
  visibility: Eye,
  subscriptions: CreditCard,
  layout: Grid3x3,
} as const

/**
 * Build lookup candidates for an icon name (exact, lowercase, snake/kebab → camelCase).
 */
function buildIconLookupCandidates(name: string): string[] {
  const trimmed = name.trim()
  const out = new Set<string>([trimmed, trimmed.toLowerCase()])

  if (trimmed.includes('_') || trimmed.includes('-')) {
    const camel = trimmed.replace(/[-_]+([a-zA-Z])/g, (_, char: string) => char.toUpperCase())
    out.add(camel)
    out.add(camel.charAt(0).toLowerCase() + camel.slice(1))
  }

  return [...out]
}

/**
 * Get icon component by backend icon name
 * @param name - Backend icon name (e.g., "dashboard", "settings", "featureToggles")
 * @returns Lucide icon component
 * @throws {Error} If icon is not found
 */
export function getIcon(name: string): LucideIcon {
  if (!name || typeof name !== 'string') {
    throw new Error(`Invalid icon name: ${name}. Icon name must be a non-empty string.`)
  }

  const candidates = buildIconLookupCandidates(name)

  for (const candidate of candidates) {
    const icon = Icons[candidate as keyof typeof Icons]
    if (icon) return icon
  }

  const normalizedName = name.trim().toLowerCase()
  const iconKeys = Object.keys(Icons)
  const matchedKey = iconKeys.find((key) => key.toLowerCase() === normalizedName)
  if (matchedKey) {
    return Icons[matchedKey as keyof typeof Icons]
  }

  const availableIcons = iconKeys.sort()
  const similarIcons = availableIcons.filter(
    (iconName) =>
      iconName.toLowerCase().includes(normalizedName) ||
      normalizedName.includes(iconName.toLowerCase())
  )

  const errorMessage =
    `Icon "${name}" not found in registry.\n` +
    `Tried: ${candidates.join(', ')}\n` +
    `Available icons (${availableIcons.length}): ${availableIcons.slice(0, 20).join(', ')}${availableIcons.length > 20 ? '...' : ''}\n` +
    (similarIcons.length > 0 ? `Similar icons: ${similarIcons.join(', ')}\n` : '') +
    `\nTo fix: Add "${name}" to the Icons registry in config/icons.map.ts`

  throw new Error(errorMessage)
}

/**
 * Check if icon exists in registry
 * Handles both camelCase and lowercase keys
 */
export function hasIcon(name: string): boolean {
  if (!name) return false

  try {
    getIcon(name)
    return true
  } catch {
    return false
  }
}

/**
 * Get all available icon names
 */
export function getAllIconNames(): string[] {
  return Object.keys(Icons)
}

